import { createApi, fakeBaseQuery } from "@reduxjs/toolkit/query/react";
import {
  createPatient,
  getPatients,
  getPatient,
  updatePatient,
  deletePatient,
  reassignPatientToTherapist,
  updatePatientTherapists,
  setPatientActive,
  getClientsWithoutGoals,
  logActivity,
  ACTIVITY_ACTIONS,
  ENTITY_TYPES,
} from "../../firebase";

// Page size for "pick a patient" dropdowns (calendar, forms, tasks, home).
// These pickers never paginate, so the fetch must cover the whole clinic.
// getPatients orders by createdAt desc + limit(pageSize): anything below the
// cap silently drops the OLDEST patients from the picker. Keep above the
// largest clinic.
export const PATIENTS_PICKER_PAGE_SIZE = 2000;

export const patientsApi = createApi({
  reducerPath: "patientsApi",
  baseQuery: fakeBaseQuery(),
  tagTypes: ["Patients", "Patient"],
  endpoints: (builder) => ({
    // Get paginated patients list
    // If therapistId is provided, returns only that therapist's patients
    // If therapistId is null/undefined, returns all clinic patients (for admin view)
    getPatients: builder.query({
      async queryFn({
        clinicId,
        search,
        status,
        cursor,
        pageSize = 10,
        therapistId,
        includeInactive = false,
      }) {
        try {
          const result = await getPatients(clinicId, {
            search,
            status,
            cursor,
            pageSize,
            therapistId,
            includeInactive,
          });
          // Return only serializable data (no Firestore DocumentSnapshots)
          return {
            data: {
              patients: result.patients,
              nextCursor: result.nextCursor,
              hasMore: result.hasMore,
            },
          };
        } catch (error) {
          console.error("[CK-7001] Error fetching patients:", {
            errorCode: "CK-7001",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result) =>
        result?.patients
          ? [
              ...result.patients.map(({ id }) => ({ type: "Patients", id })),
              { type: "Patients", id: "LIST" },
            ]
          : [{ type: "Patients", id: "LIST" }],
    }),

    // Nutrition clinics: clients created without daily goals. Lives here (not
    // in dailyReportsApi) so createPatient / updatePatient invalidate it for
    // free through the "Patients LIST" tag — no cross-API wiring.
    getClientsWithoutGoals: builder.query({
      async queryFn({ clinicId, therapistId, isAdmin }) {
        try {
          const patients = await getClientsWithoutGoals(clinicId, therapistId, {
            isAdmin,
          });
          return { data: patients };
        } catch (error) {
          console.error("[CK-7091] Error fetching clients without daily goals:", {
            errorCode: "CK-7091",
            errorMessage: error.message,
            clinicId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: [{ type: "Patients", id: "LIST" }],
    }),

    // Get single patient
    getPatient: builder.query({
      async queryFn({ clinicId, patientId }) {
        try {
          const patient = await getPatient(clinicId, patientId);
          if (!patient) {
            console.error("[CK-7002] Patient not found:", {
              errorCode: "CK-7002",
              clinicId,
              patientId,
              timestamp: new Date().toISOString(),
            });
            return { error: { message: "Patient not found" } };
          }
          return { data: patient };
        } catch (error) {
          console.error("[CK-7003] Error fetching patient:", {
            errorCode: "CK-7003",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      providesTags: (result, error, { patientId }) => [
        { type: "Patient", id: patientId },
      ],
    }),

    // Create new patient
    // therapistId is required - patient is always associated with the creating therapist
    createPatient: builder.mutation({
      async queryFn({ clinicId, patientData, therapistId, therapistName, enableAiPatientSummary }) {
        try {
          const patient = await createPatient(
            clinicId,
            patientData,
            therapistId,
            { enableAiPatientSummary }
          );

          // Log activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.CREATE_PATIENT,
            entityType: ENTITY_TYPES.PATIENT,
            entityId: patient.id,
            entityName: `${patient.firstName} ${patient.lastName}`,
            details: {
              phone: patientData.phone,
              referralReason: patientData.referralReason,
            },
            success: true,
          });

          return { data: patient };
        } catch (error) {
          console.error("[CK-7004] Error creating patient:", {
            errorCode: "CK-7004",
            errorMessage: error.message,
            clinicId,
            therapistId,
            timestamp: new Date().toISOString(),
          });

          // Log failed activity
          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.CREATE_PATIENT,
            entityType: ENTITY_TYPES.PATIENT,
            entityName: `${patientData.firstName} ${patientData.lastName}`,
            success: false,
            errorMessage: error.message,
          });

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Patients", id: "LIST" }],
    }),

    // Update patient
    updatePatient: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        patientData,
        therapistId,
        therapistName,
      }) {
        try {
          const patient = await updatePatient(clinicId, patientId, patientData);

          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.UPDATE_PATIENT,
              entityType: ENTITY_TYPES.PATIENT,
              entityId: patientId,
              entityName: `${patient.firstName} ${patient.lastName}`,
              details: {
                updatedFields: Object.keys(patientData).filter(
                  (k) => !["id", "createdAt", "updatedAt"].includes(k)
                ),
              },
              success: true,
            });
          }

          return { data: patient };
        } catch (error) {
          console.error("[CK-7005] Error updating patient:", {
            errorCode: "CK-7005",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });

          // Log failed activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.UPDATE_PATIENT,
              entityType: ENTITY_TYPES.PATIENT,
              entityId: patientId,
              success: false,
              errorMessage: error.message,
            });
          }

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Patient", id: patientId },
        { type: "Patients", id: "LIST" },
      ],
      // A rename fans out server-side to denormalized `patientName` copies
      // (see updatePatient → propagatePatientName). Refresh the caches that
      // render those copies so the new name shows without a reload.
      async onQueryStarted({ patientId, patientData }, { dispatch, queryFulfilled }) {
        const touchesName =
          patientData &&
          ["firstName", "lastName", "partner1", "partner2"].some(
            (k) => patientData[k] !== undefined
          );
        if (!touchesName) return;
        try {
          await queryFulfilled;
        } catch (_) {
          return; // mutation failed — nothing to invalidate
        }
        dispatch({
          type: "calendarApi/invalidateTags",
          payload: [
            { type: "Appointments", id: "LIST" },
            { type: "Appointments", id: "PENDING_ACTION" },
            "ConfirmationLinks",
          ],
        });
        dispatch({
          type: "financeApi/invalidateTags",
          payload: [
            "Transactions",
            "KanbanTransactions",
            "PendingCollection",
            "AppointmentsWithoutPayments",
          ],
        });
        dispatch({
          type: "treatmentsApi/invalidateTags",
          payload: [{ type: "Treatments", id: `PATIENT_${patientId}` }],
        });
        dispatch({ type: "liveSessionsApi/invalidateTags", payload: ["LiveSessions"] });
        dispatch({ type: "formsApi/invalidateTags", payload: ["SignatureLink"] });
        dispatch({
          type: "questionnairesApi/invalidateTags",
          payload: ["QuestionnaireLink"],
        });
      },
    }),

    // Reassign patient to a different therapist (admin only)
    reassignPatient: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        patientName,
        newTherapistId,
        newTherapistName,
        transferAppointments,
        therapistId,
        therapistName,
      }) {
        try {
          const result = await reassignPatientToTherapist({
            clinicId,
            patientId,
            newTherapistId,
            transferAppointments,
          });

          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.UPDATE_PATIENT,
            entityType: ENTITY_TYPES.PATIENT,
            entityId: patientId,
            entityName: patientName || "לא ידוע",
            details: {
              reassignedTo: newTherapistName || newTherapistId,
              transferAppointments,
              appointmentsTransferred: result.appointmentsTransferred || 0,
            },
            success: true,
          });

          return { data: result };
        } catch (error) {
          console.error("[CK-7010] Error reassigning patient:", {
            errorCode: "CK-7010",
            errorMessage: error.message,
            clinicId,
            patientId,
            newTherapistId,
            timestamp: new Date().toISOString(),
          });

          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.UPDATE_PATIENT,
            entityType: ENTITY_TYPES.PATIENT,
            entityId: patientId,
            entityName: patientName || "לא ידוע",
            details: { reassignedTo: newTherapistId, transferAppointments },
            success: false,
            errorMessage: error.message,
          });

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Patient", id: patientId },
        { type: "Patients", id: "LIST" },
      ],
    }),

    // Update patient therapist assignments (multi-therapist)
    updatePatientTherapistAssignments: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        patientName,
        therapistIds,
        therapistId,
        therapistName,
      }) {
        try {
          const result = await updatePatientTherapists({
            clinicId,
            patientId,
            therapistIds,
          });

          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.UPDATE_PATIENT,
            entityType: ENTITY_TYPES.PATIENT,
            entityId: patientId,
            entityName: patientName || "לא ידוע",
            details: {
              updatedTherapistIds: therapistIds,
              addedCount: result.addedCount || 0,
            },
            success: true,
          });

          return { data: result };
        } catch (error) {
          console.error("[CK-7011] Error updating patient therapists:", {
            errorCode: "CK-7011",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });

          logActivity(clinicId, {
            therapistId,
            therapistName: therapistName || "לא ידוע",
            action: ACTIVITY_ACTIONS.UPDATE_PATIENT,
            entityType: ENTITY_TYPES.PATIENT,
            entityId: patientId,
            entityName: patientName || "לא ידוע",
            details: { updatedTherapistIds: therapistIds },
            success: false,
            errorMessage: error.message,
          });

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Patient", id: patientId },
        { type: "Patients", id: "LIST" },
      ],
    }),

    // Set patient active/inactive (visibility/archive flag, orthogonal to clinical status)
    setPatientActive: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        isActive,
        therapistId,
        therapistName,
      }) {
        try {
          const patient = await setPatientActive({
            clinicId,
            patientId,
            isActive,
            therapistId,
            therapistName,
          });
          return { data: patient };
        } catch (error) {
          console.error("[CK-7020] Error setting patient active state:", {
            errorCode: "CK-7020",
            errorMessage: error.message,
            clinicId,
            patientId,
            isActive,
            timestamp: new Date().toISOString(),
          });
          return { error: { message: error.message } };
        }
      },
      invalidatesTags: (result, error, { patientId }) => [
        { type: "Patient", id: patientId },
        { type: "Patients", id: "LIST" },
      ],
    }),

    // Delete patient
    deletePatient: builder.mutation({
      async queryFn({
        clinicId,
        patientId,
        patientName,
        therapistId,
        therapistName,
      }) {
        try {
          await deletePatient(clinicId, patientId);

          // Log activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.DELETE_PATIENT,
              entityType: ENTITY_TYPES.PATIENT,
              entityId: patientId,
              entityName: patientName || "לא ידוע",
              success: true,
            });
          }

          return { data: patientId };
        } catch (error) {
          console.error("[CK-7006] Error deleting patient:", {
            errorCode: "CK-7006",
            errorMessage: error.message,
            clinicId,
            patientId,
            timestamp: new Date().toISOString(),
          });

          // Log failed activity
          if (therapistId) {
            logActivity(clinicId, {
              therapistId,
              therapistName: therapistName || "לא ידוע",
              action: ACTIVITY_ACTIONS.DELETE_PATIENT,
              entityType: ENTITY_TYPES.PATIENT,
              entityId: patientId,
              entityName: patientName || "לא ידוע",
              success: false,
              errorMessage: error.message,
            });
          }

          return { error: { message: error.message } };
        }
      },
      invalidatesTags: [{ type: "Patients", id: "LIST" }],
    }),
  }),
});

export const {
  useGetPatientsQuery,
  useGetPatientQuery,
  useGetClientsWithoutGoalsQuery,
  useCreatePatientMutation,
  useUpdatePatientMutation,
  useReassignPatientMutation,
  useUpdatePatientTherapistAssignmentsMutation,
  useSetPatientActiveMutation,
  useDeletePatientMutation,
} = patientsApi;
